import { createQueryBuilderV4 } from "@odata2ts/odata-query-builder";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { odataFetch } from "../../lib/odata-fetch";
import {
  NCContext,
  NonConformance,
  UADMCreateNonConformanceV3_12Params,
  UADMCreateNonConformanceV3_1Response,
} from "../../odata/appu4dm/AppU4DMModel";
import {
  qNonConformance,
  QNonConformance,
  QUADMCreateNonConformanceV3_1,
} from "../../odata/appu4dm/QAppU4DM";
import { Document } from "../../odata/document/DocumentModel";
import { fetchConfiguration } from "../configuration/use-configuration";
import { useAllDocuments } from "../documents/use-all-documents";
import { useWorkOrder } from "../work-order/use-work-order";
import { useWorkOrderOperation } from "../work-order/use-work-order-operation";

type CreateNCVariables = {
  mtuNIds: string[];
  failureNIds: string[];
  severity: string;
  notes?: string;
  documentsIds: string[];
};

export function useCreateNC(props?: {
  onSuccess?: (res: UADMCreateNonConformanceV3_1Response) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();
  const { workOrderOperation } = useWorkOrderOperation();
  const { workOrder } = useWorkOrder();
  const { documents } = useAllDocuments();
  const { token } = queryClient.getDefaultOptions().queries?.meta ?? {};

  const { mutate, ...mutation } = useMutation({
    mutationFn: (vars: CreateNCVariables) =>
      createNC(
        {
          workOrderOperationNId: workOrderOperation!.NId!,
          workOrderOperationName: workOrderOperation!.Name!,
          workOrderName: workOrder!.Name!,
          workOrderNId: workOrder!.NId!,
          materialNId: workOrder!.FinalMaterial!.Material!.NId!,
          documents:
            documents?.filter((x) => vars.documentsIds.includes(x.Id)) ?? [],
          ...vars,
        },
        token!
      ),
    onError: props?.onError,
    onSuccess: props?.onSuccess,
  });

  return {
    createNC: mutate,
    ...mutation,
  };
}

type Payload = {
  workOrderNId: string;
  workOrderName: string;
  workOrderOperationNId: string;
  workOrderOperationName: string;
  materialNId: string;
  documents: Document[];
} & Omit<CreateNCVariables, "documentsIds">;

async function createDeviationId(
  mtuNId: string,
  workOrderName: string,
  workOrderOperationName: string,
  token: string
) {
  const today = new Date();
  const date = `${today.getFullYear()}${today.getMonth() + 1}${today.getDate()}`;
  const id = `DEV-${date}-${mtuNId}-${workOrderName}-${workOrderOperationName}`;

  const builder = createQueryBuilderV4("NonConformance", new QNonConformance())
    .filter(
      qNonConformance.Type.contains("QUALITY").and(
        qNonConformance.NId.startsWith(id)
      )
    )
    .top(25)
    .skip(0)
    .orderBy(qNonConformance.CreatedOn.asc())
    .count(true)
    .expand("WorkOrder", "WorkOrderOperation");

  const res = await odataFetch<NonConformance>({ builder, token });

  return `${id}-${res?.["@odata.count"] ?? 0}`;
}

async function createNC(
  {
    mtuNIds,
    severity,
    notes,
    workOrderName,
    workOrderOperationName,
    workOrderNId,
    workOrderOperationNId,
    materialNId,
    failureNIds,
    documents,
  }: Payload,
  token: string
) {
  const builder = createQueryBuilderV4(
    "UADMCreateNonConformanceV3_1",
    new QUADMCreateNonConformanceV3_1()
  );

  const [nclifeCycle, id] = await Promise.all([
    fetchConfiguration("nonConformanceLifeCycleforMaterialItem", token),
    createDeviationId(mtuNIds[0], workOrderName, workOrderOperationName, token),
  ]);

  return odataFetch<
    UADMCreateNonConformanceV3_1Response,
    UADMCreateNonConformanceV3_12Params
  >({
    builder,
    token,
    method: "POST",
    body: {
      command: {
        IsAutomatic: false,
        ChangeDataCollectionProperties: null,
        ChangeOperationProperties: null,
        ChangePartProperties: null,
        NonConformanceProperties: {
          EquipmentNId: null,
          NId: id,
          NonConformanceLifecycleNId: nclifeCycle?.value?.[0].Val ?? "",
          NonConformanceType: "QUALITY",
          Notes: notes ?? null,
          Severity: severity,
          Context: NCContext.MaterialItem,
          Quantity: mtuNIds.length,
          ParentNonConformanceNId: null,
          TimeStamp: null,
        },
        DefectList: failureNIds.map((x) => ({
          DefectNId: null,
          Notes: "",
          Status: "",
          FailureNId: x,
          GroupPath: null,
        })),
        QualityNonConformanceProperties: {
          IsTriggeredByQualityInspectionFailure: false,
          MTUNIdList: mtuNIds,
          ToolNIdList: [],
          EquipmentNIdList: [],
          WorkOrderNId: workOrderNId,
          WorkOrderOperationNId: workOrderOperationNId,
          MaterialNId: materialNId,
        },
        AttachmentList: documents.map((x) => ({
          DocumentFileName: x.FileName ?? "",
          DocumentId: x.Id,
          DocumentMIMEType: x.MIMEType ?? "",
        })),
        ContainerNidList: [],
      },
    },
  });
}
